import React, {
  useMemo,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import Box from 'Components/Atoms/Box';
import NavBar from 'Components/Organisms/NavBar';
import { SettingsRounded } from 'Components/Atoms/Icons';
import Container from 'Components/Atoms/Container';
import GridItem from 'Components/Atoms/GridItem';
import IconButton from 'Components/Atoms/IconButton';
import ProductIcons from '../../Components/ProductIcons';
import { Forecast, ReservationSnippet, UserData } from 'utils/types';
import TotalReservationsCard from 'Components/Organisms/TotalReservationsCard';
import GroupsPerInterval from 'Components/Organisms/GroupsPerInterval';
import { dateHelper, findOverlaps, reservationToSnippet } from 'utils/helper';
import ShiftReservationCard from 'Components/Organisms/ShiftReservationCard';
import GridContainer from 'Components/Atoms/GridContainer';
import PieChartCard from 'Components/Molecules/PieChartCard';
import Notifications from 'Components/Organisms/Notifications';
import SelectRestaurant from 'App/Dashboard/Components/SelectRestaurant';
import SettingsDropdown from 'App/Dashboard/Components/SettingsDropdown';
import { RestaurantContext, Severity } from 'Contexts/RestaurantContext';
import { useHistory } from 'react-router';
import UtilizationByTime from 'Components/Organisms/UtilizationByTime';
import { ReservationContext } from 'Contexts/ReservationContext';
import CloseShiftModal from 'Components/Organisms/CloseShiftModal';
import useStatistics from 'CustomHooks/useStatistics';
import { Date as DateType, ReservationShift } from 'types/shifts';
import { ReservationStatus } from 'types/reservations';
import ConnectionStatus from 'App/Dashboard/Components/ConnectionStatus';
import ShiftCommentTabs from 'App/ReservationBook/Components/ShiftCommentTabs';
import useShiftComments from 'CustomHooks/useShiftComments';
import { useMediaQuery } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import Mailbox from 'App/Dashboard/Components/Mailbox';
import useLocalStorageState from 'CustomHooks/useLocalStorageState';
import ChangelogModal from 'App/Dashboard/Components/ChangelogModal';
import { ChangelogContext } from 'Contexts/ChangelogContext';
import usePinAuth from 'CustomHooks/usePinAuth';
import NotificationV03List from 'Components/Organisms/NotificationV03List';
import server from 'utils/server';
import { AuthContext } from 'Contexts/AuthContext';
import PurchaseCard from 'App/Experiences/Components/PurchaseCard';
import MissingMollie from 'App/Experiences/Components/Mollie';
import useAlert from 'CustomHooks/useAlert';
import useAfterRolesLoaded from 'CustomHooks/useAfterRolesLoaded';
import AdminNotes from 'App/Dashboard/Components/AdminNotes';
import DashboardAlerts from 'App/Dashboard/Components/AdminNotes/DashboardAlerts';
import { db, FieldValue } from 'utils/firebase';
import Card from 'Components/Molecules/Card';
import LoadingScreen from 'Components/Atoms/LoadingScreen';
import Button from 'Components/Atoms/Button';
import useSiaEmbedUrl from 'CustomHooks/useSiaEmbedUrl';
import { ThemeContext } from 'Contexts/ThemeContext';
import Modal from 'Components/Molecules/Modal';
import {
  buildSiaTodosEmbedUrl,
  clearPendingSiaUpgrade,
  setPendingSiaUpgrade,
} from 'utils/sia';
import Typography from 'Components/Atoms/Typography';
import { AppId } from 'types/shared/documents/restaurants';
import SiaUpgradeBanner from 'App/Dashboard/Components/SiaUpgradeBanner';
import PhoneAssistantPromoBanner from 'App/Dashboard/Components/PhoneAssistantPromoBanner';

export type DashboardProps = {
  user: null | UserData;
  forecast: null | Forecast;
  update?: () => void;
};

type SiaUpgradeStep = 'idle' | 'activating' | 'waiting' | 'failed';
const SIA_UPGRADE_TIMEOUT_MS = 90000;
const SIA_POST_READY_REDIRECT_DELAY_MS = 4000;

const Dashboard = ({
  user = null,
  forecast = null,
  update,
}: DashboardProps) => {
  // const [dateToModify, setDateToModify] = useState<string>("");

  const [] = useLocalStorageState('showMessage-apps', true);

  const {
    date,
    dateData,
    submitDateData,
    setDateData,
    openModal,
    setOpenModal,
  } = useStatistics('', update);

  const {
    restaurantId,
    switchRestaurant,
    products,
    apps,
    setapps,
    lightReservation,
    notificationsV03Open,
    hasSiaV02,
    siaState,
    newToast,
  } = useContext(RestaurantContext);

  const { uid } = useContext(AuthContext);
  const { darkMode } = useContext(ThemeContext);

  const { authenticate } = usePinAuth();

  const commentShifts = useShiftComments(restaurantId);

  const [showSiaWhatsNewModal, setShowSiaWhatsNewModal] = useState(false);
  const [showSiaUpgradeModal, setShowSiaUpgradeModal] = useState(false);
  const [siaUpgradeStep, setSiaUpgradeStep] = useState<SiaUpgradeStep>('idle');
  const [siaUpgradeError, setSiaUpgradeError] = useState<string | null>(null);
  const [siaUpgradeProgress, setSiaUpgradeProgress] = useState(0);

  const upgradePollIntervalRef = useRef<number | null>(null);
  const upgradePollTimeoutRef = useRef<number | null>(null);
  const upgradeProgressIntervalRef = useRef<number | null>(null);
  const upgradeRedirectTimeoutRef = useRef<number | null>(null);
  const upgradeStartedAtRef = useRef<number | null>(null);
  const upgradeReadinessCheckInProgressRef = useRef(false);

  const {
    iframeUrl: siaTodosUrl,
    loading: siaTodosLoading,
    error: siaTodosError,
    refresh: refreshSiaTodos,
  } = useSiaEmbedUrl(
    hasSiaV02 ? restaurantId : null,
    darkMode ? 'dark' : 'light',
    buildSiaTodosEmbedUrl,
  );

  const siaTodosCount = siaState.data?.open_todos ?? 0;

  const isAdmin =
    !!user?.isGastronautAdmin ||
    !!user?.restaurants?.find((r) => r.id === restaurantId)?.admin;

  const alert = useAlert();

  const {
    reservations,
    shifts,
    occassions = [],
  } = useContext(ReservationContext);

  const history = useHistory();
  const isMobile = useMediaQuery('(max-width:450px)');

  const shouldShowSiaUpgradeBanner =
    products.includes('phone') && !apps.includes('sia-phone-assistant');

  const shouldShowPhonePromoBanner =
    !products.includes('phone') && !apps.includes('sia-phone-assistant');

  const clearUpgradePolling = useCallback(() => {
    if (upgradePollIntervalRef.current) {
      window.clearInterval(upgradePollIntervalRef.current);
      upgradePollIntervalRef.current = null;
    }

    if (upgradePollTimeoutRef.current) {
      window.clearTimeout(upgradePollTimeoutRef.current);
      upgradePollTimeoutRef.current = null;
    }

    if (upgradeProgressIntervalRef.current) {
      window.clearInterval(upgradeProgressIntervalRef.current);
      upgradeProgressIntervalRef.current = null;
    }

    if (upgradeRedirectTimeoutRef.current) {
      window.clearTimeout(upgradeRedirectTimeoutRef.current);
      upgradeRedirectTimeoutRef.current = null;
    }

    upgradeStartedAtRef.current = null;
    upgradeReadinessCheckInProgressRef.current = false;
  }, []);

  const checkSiaReadiness = useCallback(async () => {
    if (!restaurantId || !uid) return false;
    if (upgradeReadinessCheckInProgressRef.current) return false;

    try {
      upgradeReadinessCheckInProgressRef.current = true;

      const response = await server.get(`/v03/siaConnector/${restaurantId}/token`, {
        headers: { uid },
      });

      return !!response?.data?.token;
    } catch {
      return false;
    } finally {
      upgradeReadinessCheckInProgressRef.current = false;
    }
  }, [restaurantId, uid]);

  const handleSiaProvisioningSuccess = useCallback(() => {
    if (!restaurantId) return;

    clearUpgradePolling();
    setSiaUpgradeStep('waiting');
    setSiaUpgradeError(null);
    setSiaUpgradeProgress(100);

    upgradeRedirectTimeoutRef.current = window.setTimeout(() => {
      clearPendingSiaUpgrade(restaurantId);
      setSiaUpgradeStep('idle');
      setSiaUpgradeError(null);
      setShowSiaUpgradeModal(false);
      history.push(`/${restaurantId}/settings/sia-embed`);
    }, SIA_POST_READY_REDIRECT_DELAY_MS);
  }, [clearUpgradePolling, history, restaurantId]);

  const startSiaReadinessPolling = useCallback(() => {
    if (!restaurantId || !uid) {
      setSiaUpgradeStep('failed');
      setSiaUpgradeError('Aktivierung fehlgeschlagen. Bitte später erneut versuchen.');
      return;
    }

    clearUpgradePolling();
    setSiaUpgradeStep('waiting');
    setSiaUpgradeError(null);
    setSiaUpgradeProgress(3);
    upgradeStartedAtRef.current = Date.now();

    const tick = async () => {
      const ready = await checkSiaReadiness();
      if (ready) {
        setSiaUpgradeProgress(100);
        handleSiaProvisioningSuccess();
      }
    };

    upgradeProgressIntervalRef.current = window.setInterval(() => {
      const startedAt = upgradeStartedAtRef.current;
      if (!startedAt) return;

      const elapsed = Date.now() - startedAt;
      const progress = Math.min(
        96,
        Math.round((elapsed / SIA_UPGRADE_TIMEOUT_MS) * 96),
      );

      setSiaUpgradeProgress((current) => (progress > current ? progress : current));
    }, 250);

    upgradePollIntervalRef.current = window.setInterval(tick, 2500);
    tick();

    upgradePollTimeoutRef.current = window.setTimeout(() => {
      clearUpgradePolling();
      setSiaUpgradeProgress(100);
      setSiaUpgradeStep('failed');
      setSiaUpgradeError(
        'Sia wird noch vorbereitet. Bitte versuche es gleich erneut.',
      );
    }, SIA_UPGRADE_TIMEOUT_MS);
  }, [
    checkSiaReadiness,
    clearUpgradePolling,
    handleSiaProvisioningSuccess,
    restaurantId,
    uid,
  ]);

  const handleCloseSiaUpgradeModal = useCallback(() => {
    clearUpgradePolling();
    setShowSiaUpgradeModal(false);
    setSiaUpgradeStep('idle');
    setSiaUpgradeError(null);
    setSiaUpgradeProgress(0);
  }, [clearUpgradePolling]);

  const handleSiaUpgrade = useCallback(async () => {
    if (!restaurantId) return;

    if (!isAdmin) {
      alert({
        title: 'Keine Admin-Rechte',
        description:
          'Du hast keine Admin-Rechte. Nur Admins können das SIA-Upgrade aktivieren.',
      });
      return;
    }

    setShowSiaUpgradeModal(true);
    setSiaUpgradeStep('activating');
    setSiaUpgradeError(null);
    setSiaUpgradeProgress(0);

    try {
      await db.collection('restaurantData').doc(restaurantId).update({
        apps: FieldValue.arrayUnion('sia-phone-assistant'),
      });

      setapps((currentApps) =>
        currentApps.includes('sia-phone-assistant' as AppId)
          ? currentApps
          : [...currentApps, 'sia-phone-assistant' as AppId],
      );

      setPendingSiaUpgrade(restaurantId);
      startSiaReadinessPolling();
    } catch {
      clearUpgradePolling();
      setSiaUpgradeProgress(100);
      setSiaUpgradeStep('failed');
      setSiaUpgradeError(
        'Die Aktivierung ist fehlgeschlagen. Bitte versuche es erneut.',
      );
    }
  }, [alert, clearUpgradePolling, isAdmin, restaurantId, setapps, startSiaReadinessPolling]);

  const handleSiaUpgradeWhenReady = useAfterRolesLoaded(handleSiaUpgrade);

  useEffect(() => {
    return () => clearUpgradePolling();
  }, [clearUpgradePolling]);

  const handleAction = (
    date: string,
    target: 'close' | 'terrace' | 'optimize',
    open?: boolean,
  ) => {
    // console.log('date To change', date);
    // console.log('dateData', dateData);
    if (target === 'close') {
      setOpenModal(date);
    } else if (target === 'terrace') {
      const toggleTerrace = (moveReservations: boolean) => {
        server
          .get<{
            reseatedReservations?: number;
            success: boolean;
          }>(
            `/v03/reservations/toggleTerrace/${restaurantId}/${date}/${uid}?moveReservations=${
              moveReservations ? 'true' : 'false'
            }`,
          )
          .then(({ data }) => {
            update?.();

            if (moveReservations) {
              const reseatedReservations = data.reseatedReservations ?? 0;

              newToast(
                reseatedReservations
                  ? `${reseatedReservations} Reservierungen wurden verschoben`
                  : 'Terrasse wurde aktualisiert, aber keine Reservierungen wurden verschoben',
                reseatedReservations ? Severity.SUCCESS : Severity.WARNING,
              );
            } else {
              newToast('Terrasse wurde aktualisiert', Severity.SUCCESS);
            }
          })
          .catch((error) => {
            newToast(
              error?.response?.data?.message || error.message || 'error500',
              Severity.ERROR,
              'errors',
            );
          });
      };

      if (open) {
        alert({
          title: 'Terrasse schließen?',
          description:
            'Wenn du fortführst wird die Terrasse geschlossen. Möchtest du fortfahren?',
          secondaryAction: {
            label: 'Reservierung nach innen verschieben?',
            default: false,
          },
          onSubmit: (secAction) => {
            toggleTerrace(!!secAction);
          },
        });
      } else if (typeof open === 'boolean' && !open) {
        alert({
          title: 'Terrasse öffnen?',
          description:
            'Wenn du fortführst wird die Terrasse geöffnet. Möchtest du fortfahren?',
          secondaryAction: {
            label: 'Reservierung auf Terrasse verschieben?',
            default: false,
          },
          onSubmit: (secAction) => {
            toggleTerrace(!!secAction);
          },
        });
      } else {
        toggleTerrace(false);
      }
    } else if (target === 'optimize') {
      alert({
        title: 'Reservierungen optimieren',
        description:
          'Reservierung automatisch umplatzieren? Dadurch können mehr Gäste Platz finden.',
        onSubmit: () => {
          server
            .get(
              `/v03/reservations/optimizeReservations/${restaurantId}/${date}/${uid}`,
            )
            .then(() => {
              update?.();
            });
        },
      });
    }
  };

  const reservationSnippets = useMemo(
    () =>
      reservations
        .filter((r) => !r.blockedFullShift && r.guests)
        .map(reservationToSnippet),
    [reservations],
  );

  const { richShifts, otherReservations } = useMemo(() => {
    let richShifts: (ReservationShift & {
      reservations: ReservationSnippet[];
    })[] = [];
    let resaIds: string[] = [];

    (shifts ?? []).forEach((s) => {
      let filteredRes = reservations.filter(
        (r) =>
          !r.blockedFullShift &&
          (s.occassions?.includes(r.occassion) ||
            s.spaces?.includes(r?.space ?? '')) &&
          findOverlaps(r, s.start || 0, s.close || 0, true) &&
          r.status !== ReservationStatus.FAILED,
      );

      richShifts.push({
        ...s,
        reservations: filteredRes.map(reservationToSnippet),
      });

      resaIds.push(...filteredRes.map((r) => r.id));
    });

    let otherReservations = reservations
      .filter(
        (r) => !resaIds.includes(r.id) && r.status !== ReservationStatus.FAILED,
      )
      .map(reservationToSnippet);

    console.log({ otherReservations });

    return { richShifts, otherReservations };
  }, [reservations, shifts]);

  const reservationsByOccassions = useMemo(() => {
    return occassions
      .map((occ) => ({
        value: reservationSnippets
          .filter(
            (res) =>
              res.occassion === occ.id &&
              res.status !== ReservationStatus.FAILED,
          )
          .reduce((acc: number, cV) => acc + cV.guests, 0),
        label: occ.title,
        id: occ.id,
      }))
      .filter((x) => !!x.value);
  }, [reservationSnippets, occassions]);

  const reservationBySource = useMemo(() => {
    let sourceTable = reservationSnippets
      .filter((res) => res.status !== ReservationStatus.FAILED)
      .reduce((acc: any, cV) => {
        if (!acc[cV.source]) {
          acc[cV.source] = cV.guests;
        } else {
          acc[cV.source] += cV.guests;
        }

        return acc;
      }, {});

    return Object.keys(sourceTable)
      .map((id) => ({
        id,
        label: id,
        value: sourceTable[id],
      }))
      .filter((x) => !!x.value);
  }, [reservationSnippets]);

  const userRestaurants = user?.restaurants || [];

  const handleRestaurantChange = (rId: string) => {
    switchRestaurant(rId);
    history?.push(`/${rId}`);
  };

  const handleSettingsClick = (rId: string) => {
    switchRestaurant(rId);
    history?.push(`/${rId}/settings`);
  };

  // const [siaSettings] = useDocument<SiaSettingsDocument>(
  //   `/restaurants/${restaurantId}/settings`,
  //   'siaSettingsV02'
  // );

  const totalReservations = reservationSnippets
    .filter((r) => r.status !== ReservationStatus.FAILED)
    .reduce((acc, cV) => acc + cV.guests, 0);

  const {
    allShowingChangelogs,
    showChangelog,
    setshowChangelog,
    updateProfilesLatestVersion,
  } = useContext(ChangelogContext);

  // @Leon
  // this is written just for the Pincode modal to open
  // delete upon completion

  const whatsNewOverlayPadding = isMobile ? 16 : 24;
  const whatsNewCloseOffset = isMobile ? 16 : 24;
  const whatsNewVideoWidth = isMobile
    ? '94vw'
    : 'min(80vw, calc(80vh * 16 / 9))';

  return (
    <Box background style={{ display: 'flex', alignItems: 'stretch' }}>
      <Box style={{ overflow: 'scroll', height: '100vh' }}>
        <NavBar
          title="Dashboard"
          hideMenu
          right={
            <>
              <Notifications />
              {user !== null && !isMobile && <SettingsDropdown user={user} />}
            </>
          }
        />

        {user && showChangelog && (
          <ChangelogModal
            isOpen={showChangelog}
            onClose={() => setshowChangelog(false)}
            data={allShowingChangelogs}
            onOk={() => updateProfilesLatestVersion(user?.uid ?? '')}
          />
        )}

        {showSiaWhatsNewModal && (
          <Box
            className="sia-whats-new-portal"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 2000,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: whatsNewOverlayPadding,
            }}
            onClick={() => setShowSiaWhatsNewModal(false)}
          >
            <IconButton
              className="sia-whats-new-close"
              onClick={() => setShowSiaWhatsNewModal(false)}
              aria-label="Close video"
              style={{
                position: 'absolute',
                top: whatsNewCloseOffset,
                right: whatsNewCloseOffset,
                width: 44,
                height: 44,
                borderRadius: '999px',
                background: 'rgba(255, 255, 255, 0.12)',
                color: '#fff',
              }}
            >
              <Close />
            </IconButton>
            <Box
              className="sia-whats-new-video-shell"
              style={{
                width: whatsNewVideoWidth,
                maxWidth: isMobile ? '94vw' : '80vw',
                maxHeight: '80vh',
                aspectRatio: '16 / 9',
                borderRadius: 14,
                overflow: 'hidden',
                background: '#000',
                boxShadow: '0 30px 80px rgba(0, 0, 0, 0.4)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                title="SIA Was ist neu?"
                src="https://www.youtube.com/embed/EDlqFl16a5Q"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block',
                }}
              />
            </Box>
          </Box>
        )}

        <Modal
          open={showSiaUpgradeModal}
          onClose={handleCloseSiaUpgradeModal}
          stopAwayClick={siaUpgradeStep !== 'failed'}
          title="Sia wird aktiviert"
          actions={[
            {
              id: 'later',
              label: 'Später',
              onClick: handleCloseSiaUpgradeModal,
            },
            ...(siaUpgradeStep === 'failed'
              ? [
                  {
                    id: 'retry',
                    label: 'Erneut prüfen',
                    variant: 'primary' as const,
                    onClick: startSiaReadinessPolling,
                  },
                ]
              : []),
          ]}
        >
          <Box className="sia-upgrade-modal-content">
            {(siaUpgradeStep === 'activating' || siaUpgradeStep === 'waiting') && (
              <Box className="sia-upgrade-modal-loading">
                <div
                  className="sia-upgrade-progress-track"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={siaUpgradeProgress}
                >
                  <div
                    className="sia-upgrade-progress-fill"
                    style={{ width: `${siaUpgradeProgress}%` }}
                  />
                </div>
                <Typography variant="text-4" className="sia-upgrade-progress-label">
                  {siaUpgradeProgress}%
                </Typography>
                <Typography variant="text-3" textAlign="center" block>
                  {siaUpgradeProgress >= 100
                    ? 'Sia ist bereit. Wir öffnen den Bereich in wenigen Sekunden.'
                    : 'Sia wird aktiviert. Das kann bis zu 90 Sekunden dauern.'}
                </Typography>
              </Box>
            )}
            {siaUpgradeStep === 'failed' && (
              <Box className="sia-upgrade-modal-error">
                <Typography color="error" variant="text-3">
                  {siaUpgradeError}
                </Typography>
              </Box>
            )}
          </Box>
        </Modal>

        <Container
          className="homeScreen"
          style={{ width: notificationsV03Open ? 'calc(100vw - 378px)' : '' }}
        >
          <GridItem xs={12}>
            <Box flex className="space-between" style={{ marginTop: 32 }}>
              <SelectRestaurant
                restaurantId={restaurantId}
                restaurants={userRestaurants}
                onRestaurantChange={handleRestaurantChange}
                onSettingsClick={handleSettingsClick}
              />
              {isAdmin && (
                <IconButton
                  size="large"
                  disabled={!restaurantId}
                  target="_blank"
                  color="subdued"
                  aria-label="Settings"
                  onClick={() =>
                    authenticate('dashboard.settings.read', () =>
                      history?.push(`/${restaurantId}/settings`),
                    )
                  }
                >
                  <SettingsRounded color="inherit" />
                </IconButton>
              )}
            </Box>
          </GridItem>

          <DashboardAlerts restaurantId={restaurantId} />

          {/* {showMessage && (
            <GridItem xs={12}>
              <Alert
                severity="info"
                style={{ fontSize: '90%' }}
                onClose={() => setShowMessage(false)}
              >
                <AlertTitle style={{ fontSize: '110%', fontWeight: 'bolder' }}>
                  Neue Features in den Einstellungen!
                </AlertTitle>
                Reduziere No-Shows mit einer Kreditkartenabfrage, integriere
                deine Reservierungen in soziale Medien wie Instagram und
                Facebook und fülle kurzfristige Stornos effizient wieder auf,
                mithilfe der Warteliste!
              </Alert>
            </GridItem>
          )} */}

          {/* <RichAd
            id="addons-2"
            title="Verkauft Extras im Reservierungsprozess "
            image="/cash.png"
            imageStyle={{
              width: 200,
              position: 'absolute',
              right: '0',
              top: '39px',
              zIndex: 0,
            }}
            button={{
              text: 'Bis zu 3000€ mehr Umsatz im Monat',
              onClick: () => {
                history.push(`/${restaurantId}/experiences`);
              },
            }}
            description={
              'Nutze unser neuestes Feature "Add-Ons", um neue Extras wie ein <strong>Candle Light Dinner, garantierter Fensterplatz, Chefstable, Infinity Rosen, Geburtstagskuchen, Aperitif und vieles mehr</strong> anzubieten. Lass dich von unseren Vorlagen inspirieren und generiere damit neuen nie zuvor dagewesenen Umsatz!'
            }
          /> */}

          {shouldShowSiaUpgradeBanner && (
            <SiaUpgradeBanner
              onUpgrade={handleSiaUpgradeWhenReady}
              onWhatsNew={() => setShowSiaWhatsNewModal(true)}
            />
          )}

          {shouldShowPhonePromoBanner && <PhoneAssistantPromoBanner />}

          <MissingMollie restaurantId={restaurantId || ''} />

          <GridItem xs={12}>
            <ProductIcons products={products} restaurantId={restaurantId} />
          </GridItem>

          {/*hasSiaV02 && liveCallsWithReservations.length > 0 && (
            <GridItem xs={12}>
              <Card
                title="SIA Live Calls"
                headerRight={
                  <Box
                    className="sia-card-header-right"
                    style={{ width: 'fit-content' }}
                  >
                    <Box className="sia-card-badge">
                      {liveCallsWithReservations.length}
                    </Box>
                  </Box>
                }
              >
                <Box className="sia-live-calls-card">
                  {liveCallsWithReservations.map((call) => {
                    const startedAt = call.startedAt
                      ? new Date(call.startedAt)
                          .toLocaleTimeString('de-DE')
                          .slice(0, 5)
                      : null;

                    return (
                      <Box key={call.id} className="sia-live-call">
                        <Box className="sia-live-call-header">
                          <Box>
                            <Typography variant="text-3" weight="bold" block>
                              {call.name
                                ? `${call.name} (${call.callerId})`
                                : call.callerId}
                            </Typography>
                            <Typography variant="text-4" color="subdued">
                              {startedAt ? `Live • Start ${startedAt}` : 'Live'}
                            </Typography>
                          </Box>
                          <span className="sia-live-pill">Live</span>
                        </Box>
                        <Box className="sia-live-call-reservations">
                          {(call.reservations ?? []).map((reservation) => (
                            <Button
                              key={reservation.id}
                              variant="outlined"
                              className="sia-live-reservation"
                              onClick={() => {
                                if (!restaurantId) return;
                                history.push(
                                  `/${restaurantId}/reservations/${reservation.date}/tablePlan?reservationId=${reservation.id}`,
                                );
                              }}
                            >
                              <Box className="sia-live-reservation-main">
                                <Typography
                                  variant="text-4"
                                  weight="bold"
                                  block
                                >
                                  {reservation.name ||
                                    reservation.customId ||
                                    'Reservation'}
                                </Typography>
                                <Typography variant="text-5" color="subdued">
                                  {reservation.guests} PAX • {reservation.time}
                                  {reservation.occassion
                                    ? ` • ${reservation.occassion}`
                                    : ''}
                                </Typography>
                              </Box>
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Card>
            </GridItem>
          )*/}

          {hasSiaV02 && !!siaTodosCount && (
            <GridItem xs={12}>
              <Card
                title="SIA To-dos"
                headerRight={
                  <Box
                    className="sia-card-header-right"
                    style={{ width: 'fit-content' }}
                  >
                    <Box className="sia-card-badge">{siaTodosCount}</Box>
                  </Box>
                }
                closable
                opened={false}
                bodyProps={{ padding: 0 }}
              >
                <Box className="sia-todos-embed">
                  {siaTodosLoading && (
                    <Box className="sia-todos-loading">
                      <LoadingScreen loading />
                    </Box>
                  )}
                  {!siaTodosLoading && !!siaTodosError && (
                    <Box className="sia-todos-error">
                      <Typography color="error">{siaTodosError}</Typography>
                      <Button onClick={() => refreshSiaTodos()}>
                        Erneut versuchen
                      </Button>
                    </Box>
                  )}
                  {!siaTodosLoading && !siaTodosError && !!siaTodosUrl && (
                    <iframe
                      title="SIA To-dos"
                      src={siaTodosUrl}
                      className="sia-todos-iframe"
                      allow="microphone"
                      style={{ width: '100%', height: '400px', border: 'none' }}
                    />
                  )}
                </Box>
              </Card>
            </GridItem>
          )}

          <ConnectionStatus />

          {products.includes('phone') && <Mailbox />}

          {products.includes('addOns') && !!restaurantId && (
            <GridItem xs={12}>
              <PurchaseCard restaurantId={restaurantId} />
            </GridItem>
          )}

          {(products.includes('reservation') ||
            products.includes('light-reservation')) && (
            <>
              <GridItem
                xs={12}
                {...(notificationsV03Open ? { lg: 7, md: 12 } : { md: 7 })}
              >
                {!!restaurantId &&
                  !!commentShifts.filter((x) => x.comment).length && (
                    <ShiftCommentTabs
                      small
                      shiftOptions={commentShifts
                        .filter((x) => !!x.comment)
                        .map((x) => ({
                          id: x.shift.id,
                          label: x.shift.name || '',
                          shift: x.shift,
                          comment: x.comment,
                        }))}
                      restaurantId={restaurantId}
                      date={date}
                      style={{ marginBottom: 16 }}
                    />
                  )}

                <TotalReservationsCard
                  totalReservations={totalReservations}
                  forecast={forecast}
                  handleAction={handleAction}
                />
                <GroupsPerInterval
                  shifts={richShifts}
                  otherReservations={otherReservations}
                  style={{ marginBottom: 16 }}
                />
                <UtilizationByTime
                  shifts={richShifts}
                  otherReservations={otherReservations}
                />
              </GridItem>

              <GridItem
                xs={12}
                {...(notificationsV03Open ? { lg: 5, md: 12 } : { md: 5 })}
              >
                <GridContainer style={{ paddingBottom: 16 }}>
                  <GridItem
                    xs={12}
                    {...(notificationsV03Open ? { lg: 6, md: 12 } : { md: 6 })}
                  >
                    <PieChartCard
                      title="Occassions"
                      data={reservationsByOccassions}
                      style={{ height: '100%' }}
                    />
                  </GridItem>
                  <GridItem
                    xs={12}
                    {...(notificationsV03Open ? { lg: 6, md: 12 } : { md: 6 })}
                  >
                    <PieChartCard
                      title="Sources"
                      data={reservationBySource}
                      style={{ height: '100%' }}
                    />
                  </GridItem>
                </GridContainer>
                <ShiftReservationCard
                  reservations={reservationSnippets}
                  restaurantId={restaurantId ?? ''}
                  date={date || dateHelper()}
                  lightReservation={lightReservation}
                />
              </GridItem>
            </>
          )}
          {!products.includes('reservation') &&
            !products.includes('light-reservation') && (
              <GridItem
                xs={12}
                {...(notificationsV03Open ? { lg: 6, md: 12 } : { md: 6 })}
                style={{ height: '50vh' }}
              />
            )}
        </Container>
        {!!dateData && openModal && (
          <CloseShiftModal
            open={!!openModal}
            onClose={() => setOpenModal(null)}
            date={openModal ?? ''}
            onSubmit={submitDateData}
            dateData={dateData}
            onChange={(nV: DateType) => setDateData(nV)}
          />
        )}
      </Box>
      <NotificationV03List />
      {!!user?.isGastronautAdmin && !!restaurantId && (
        <AdminNotes restaurantId={restaurantId} />
      )}
    </Box>
  );
};

export default Dashboard;
